/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useRef } from 'react';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Theme as MaterialUITheme } from '@rjsf/material-ui';
import LoadingPreview from 'components/submission/LoadingPreview';
import withTheme from 'components/submission/rjsf/withTheme';
import { fields, templates, widgets } from 'components/submission/templates';
import { handleBusinessRuleValidations } from 'shared/configuration/businessRules';
import { handleCancelError } from '../../api/helpers/api.helpers';

const MuiForm = withTheme(MaterialUITheme);

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  heading: {
    marginBottom: '4rem',
  },
  title: {
    marginBottom: '1rem',
  },
  description: {
    fontWeight: 400,
    marginBottom: '1.5rem',
  },
  paper: {
    margin: '1.5rem 0',
    padding: '1rem 1rem',
    backgroundColor: theme.palette.elementBackground,
    boxShadow: 'none',
    borderRadius: '1rem',

    [theme.breakpoints.up('md')]: {
      padding: '2rem 3rem',
    },
    [theme.breakpoints.up('xl2')]: {
      width: '75%',
      maxWidth: '60rem',
    },
  },
  whitePaper: {
    marginBottom: '4rem',
    padding: '1rem 1rem',
    backgroundColor: theme.palette.white,
    boxShadow: '0rem 0.0625rem 0.1875rem rgba(63, 63, 68, 0.15)',
    borderRadius: '0.25rem',
    [theme.breakpoints.up('md')]: {
      padding: '2rem 3rem',
    },
    [theme.breakpoints.up('xl2')]: {
      width: '75%',
      maxWidth: '60rem',
    },
  },
  sectionDivider: {
    borderBottom: '0.1rem solid grey',
    marginBottom: theme.spacing(2),
  },
  sectionTopIndent: {
    paddingBottom: theme.spacing(1),
  },
  lastSectionIndent: {
    marginBottom: theme.spacing(10),
  },
  sectionBottomIndent: {
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    marginRight: theme.spacing(1),
  },
  bottomBar: {
    backgroundColor: 'rgba(0,0,0,0.1)',
    top: 'auto',
    bottom: 0,
  },
  textField: {
    minWidth: '14rem',
    [theme.breakpoints.up('md')]: {
      minWidth: '19rem',
    },
  },
}));

function AutofillForm({
  submission,
  procedure,
  validationSchema,
  uiSchema,
  setUiSchema,
  setSubmission,
  setIsValid,
  extraErrors,
  setExtraErrors,
  submissionId,
  dispatchDraftState,
  testId,
}) {
  const idPrefix = 'autofill-form';
  const showForm = !isEmpty(validationSchema) && !isEmpty(uiSchema);

  const classes = useStyles();
  const { t } = useTranslation();

  const formRef = useRef(null);

  const onChange = async (state, signal) => {
    try {
      const {
        workflowValidations: newWorkflowValidations,
        uiSchema: newUiSchema,
      } = await handleBusinessRuleValidations(
        { ...state.formData, procedure: procedure.id, receivingOffice: procedure.receivingOffice },
        uiSchema,
        'autofill', // TODO: add 'dialog:xxx' props in uiSchema in a future refactor (see {procedure}/sa/uiSchema.json)
        'autofill', // TODO: add 'dialog:xxx' props in uiSchema in a future refactor (see {procedure}/sa/uiSchema.json),
        signal,
      );
      setUiSchema(newUiSchema);
      setSubmission({ ...state.formData });
      setIsValid(newWorkflowValidations.isValid);
      setExtraErrors(newWorkflowValidations.extraErrors);
    } catch (error) {
      handleCancelError(error);
    }
  };

  return (
    <div data-testid={testId}>
      {showForm ? (
        <MuiForm
          ref={formRef}
          translation={t}
          className={classes.form}
          idPrefix={idPrefix}
          id={idPrefix}
          schema={validationSchema}
          uiSchema={uiSchema}
          fields={fields}
          widgets={widgets}
          templates={templates}
          formData={submission}
          formContext={{
            classes,
            dispatchDraftState,
            submissionId,
            schema: validationSchema,
          }}
          onChange={onChange}
          onBlur={onChange}
          disabled={false}
          showErrorList={false}
          extraErrors={extraErrors}
          /* eslint-disable react/no-children-prop */
          children={<br />}
        />
      ) : (
        <LoadingPreview />
      )}
    </div>
  );
}

export default React.memo(AutofillForm);
